import {PAGE_LOAD_TYPE} from '../enums/PageLoadType';
import {ErrorSeverity} from '../types/AnalyticsConfig';
import type {CustomDataValues} from '../types/CustomDataValues';
import * as CustomDataUtils from '../types/CustomDataValues';

import {isDocumentHidden} from './documentUtils';
import {endsWith, startsWith} from './stringUtils';

/**
 * Performs a loose equality check (==), which means it checks for both null and undefined.
 *
 * @returns {value is null | undefined} `true` if the given value is `null` or `undefined`.
 */
export const isNullish = (value: unknown): value is null | undefined => value == null;

/**
 *
 * @returns {value is Exclude<unknown, null | undefined>} `true` if the given value is not `null` and is not `undefined`.
 */
export const isNotNullish = <T = unknown>(value: T): value is Exclude<T, null | undefined> => !isNullish(value);

/**
 * @returns {value is boolean} `true` if the given value is {@link isNotNullish} and is type of `boolean`.
 */
export const isBoolean = (value: unknown): value is boolean => !isNullish(value) && typeof value === 'boolean';

export const toBoolean = (value: unknown): boolean => Boolean(value).valueOf();

/**
 * @returns {value is number} `true` if the given value is {@link isNotNullish} and is type of `number`.
 */
export const isNumber = (value: unknown): value is number => !isNullish(value) && typeof value === 'number';

/**
 * Checks if a value is a non-null object.
 *
 * We must exclude `null` explicitly, because `typeof null === "object"`
 *
 * @returns {value is Record<string, unknown>} `true` if the value is a non-null object, otherwise `false`.
 */
export const isObject = <T extends object>(value: T): value is T => {
  return isNotNullish(value) && typeof value === 'object';
};

/**
 * Checks if a value is a plain object (not an array, function, or built-in instance).
 *
 * @returns {value is Record<string, unknown>} `true` if the value is a plain object, otherwise `false`.
 */
export const isPlainObject = <T extends object>(value: T): value is T => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

/**
 * Checks if a value is a function.
 *
 * @returns {value is function} `true` if the value is a function, otherwise `false`.
 */
export const isFunction = <T extends (...args: unknown[]) => unknown>(value: unknown): value is T =>
  typeof value === 'function';

/**
 * Checks if a value is of type ErrorSeverity.
 *
 * @returns {value is ErrorSeverity} `true` if the value is of type ErrorSeverity, otherwise `false`.
 */
export function isErrorSeverity(anything: unknown): anything is ErrorSeverity {
  // invalid, when not defined as one of string values we expect
  return anything === 'CRITICAL' || anything === 'INFO';
}

export const sanitizePath = (path: string): string => {
  return path.replace(/\/$/g, '');
};

/** Take a time in seconds and return a value that represents a time in milliseconds, rounded to the nearest integer */
export const calculateTime = (timeInSeconds: number) => Math.round(timeInSeconds * 1000);

/** Same as {@link calculateTime} but if time is "nullish", then the return value is `undefined` */
export const calculateTimeWithUndefined = (timeInSeconds: number | null | undefined): number | undefined => {
  if (isNullish(timeInSeconds)) {
    return undefined;
  }
  return calculateTime(timeInSeconds);
};

/**
 * @deprecated Use directly `Date.now()` instead
 *
 * Return the current time in milliseconds.
 */
export const getCurrentTimestamp = (): number => {
  return Date.now();
};

/**
 * Empty function returns undefined
 *
 * @deprecated
 */
export const noOp = () => undefined;

export const getCustomDataString = (customData: any): string | undefined => {
  if (typeof customData === 'object') {
    return JSON.stringify(customData);
  } else if (isFunction(customData)) {
    return getCustomDataString(customData());
  } else if (typeof customData === 'undefined') {
    return customData;
  } else if (typeof customData !== 'string') {
    return String(customData);
  }

  return customData;
};

export const transferCustomDataFields = (fromConfig: CustomDataValues, toConfig: CustomDataValues) => {
  for (let i = 1; i <= CustomDataUtils.customDataFieldCount; i++) {
    const customDataField = `customData${i}`;
    toConfig[customDataField] = getCustomDataString(fromConfig[customDataField]);
  }
};

/**
 * Determine if the page was loaded in the foreground or background
 */
export function getPageLoadType(): PAGE_LOAD_TYPE {
  if (isDocumentHidden()) {
    return PAGE_LOAD_TYPE.BACKGROUND;
  }
  return PAGE_LOAD_TYPE.FOREGROUND;
}

export const calculatePercentage = (numerator?: number, denominator?: number): number | undefined => {
  if (denominator === undefined || denominator === 0) {
    return undefined;
  }
  return Math.round(((numerator || 0) / denominator) * 100);
};

export const getURLResourceName = (url: string): string => {
  if (url.length === 0) {
    return '';
  }
  return url.split('/').pop() || '';
};

export const joinUrls = (...args: string[]): string => {
  return args.reduce((prev, current) => {
    const prevUrlValue = endsWith(prev, '/') && current !== undefined ? prev.substr(0, prev.length - 1) : prev;
    const nextUrlValue = startsWith(current, '/') ? current.substr(1) : current;
    return prevUrlValue + '/' + nextUrlValue;
  });
};

/**
 * Detect if running in a React Native environment using navigator.product
 * https://github.com/facebook/react-native/commit/3c65e62183ce05893be0822da217cb803b121c61
 * https://github.com/facebook/react-native/issues/1331
 * https://stackoverflow.com/questions/39468022/how-do-i-know-if-my-code-is-running-as-react-native
 * navigator object from vega os simulator {"product":"ReactNative","userAgent":"ReactNative"}
 */
export const isReactNative = (): boolean => {
  // navigator.product is deprecated, but it remains the standard way to detect React Native environments
  return isNotNullish(navigator) && navigator.product === 'ReactNative';
};
